import { Divider, makeStyles, withStyles } from '@material-ui/core'
import UpdateIcon from '@material-ui/icons/Update'
import get from 'lodash/get'
import { DateTime } from 'luxon'
import {
  ArrayField,
  AutocompleteArrayInput,
  Button,
  ChipField,
  Datagrid,
  DateField,
  DateTimeInput,
  Filter,
  FunctionField,
  Labeled,
  NumberField,
  ResourceContextProvider,
  SelectInput,
  Show,
  SimpleShowLayout,
  SingleFieldList,
  TextField,
  TextInput,
  useRecordContext,
  useRedirect,
} from 'react-admin'
import { ExternalLink } from 'shared/components/ExternalLink/ExternalLink'
import { getAppUrl } from 'shared/config/config'

import { CopyToClipboardButton } from 'components/CopyToClipboardButton/CopyToClipboardButton'
import ListPagination from 'components/Pagination'
import { US_STATES } from '../../helpers/states'
import { CustomBooleanField, StatusTextField } from '../Fields'
import CTList from '../List'
import { SearchRecordtoForm } from './SearchCreate'
import { CustomReferenceDateField } from './SearchList'

const dateParser = (value: string) => {
  const date = new Date(value)
  return value ? date.toISOString() : null
}

const useFilterStyles = makeStyles({
  form: {
    'margin-top': 0,
  },
})

const InputProps = {
  alwaysOn: true,
  InputLabelProps: {
    shrink: true,
  },
}

const STATUS_NUMBER_STYLES = {
  true: {
    'font-weight': 'bold',
    'font-style': 'italic',
  },
}

const StatusNumberField = withStyles(STATUS_NUMBER_STYLES)(({ classes, ...props }: any) => {
  const status_name = props.record[props.status_source]
  return <NumberField className={classes[status_name]} {...props} />
})

const LocalizedDateTimeField = ({ timezone_field, options, ...props }: any) => {
  return (
    <FunctionField
      {...props}
      render={(record: any) => {
        if (!record[props.source]) {
          return null
        }

        const timezone = get(record, timezone_field, 'America/Los_Angeles')
        const newOptions = { timeZone: timezone, ...options }
        const datetime = DateTime.fromISO(record[props.source]).setZone(timezone)

        return datetime.toLocaleString(newOptions)
      }}
    />
  )
}

const LoadFilters = (props: any) => {
  const classes = useFilterStyles()
  return (
    <Filter classes={classes} {...props}>
      <TextInput source="label" label="Label" {...InputProps} />
      <TextInput source="search" label="Broker" {...InputProps} />
      <DateTimeInput
        source="qs_pickup_window_end__gt"
        label="Pickup Later Than"
        parse={dateParser}
        alwaysOn
      />
      <DateTimeInput
        source="qs_pickup_window_start__lt"
        label="Pickup Earlier Than"
        parse={dateParser}
        alwaysOn
      />
      <DateTimeInput
        source="qs_delivery_window_end__gt"
        label="Delivery Later Than"
        parse={dateParser}
        alwaysOn={false}
      />
      <DateTimeInput
        source="qs_delivery_window_start__lt"
        label="Delivery Earlier Than"
        parse={dateParser}
        alwaysOn={false}
      />
      <TextInput source="qs_origin_deadhead_mi__lt" label="Max. Origin Deadhead" {...InputProps} />
      <TextInput source="qs_loaded_mi__gt" label="Min. Loaded Miles" {...InputProps} />
      <TextInput source="qs_total_mi__lt" label="Max. Total Miles" {...InputProps} />
      <TextInput source="qs_trip_rate__gt" label="Min. Trip Rate" {...InputProps} />
      <TextInput source="qs_rate_per_total_mi__gt" label="Min. Rate Per Mile" {...InputProps} />
      <SelectInput
        label="Instant Book"
        source="qs_instant_book"
        choices={[
          { id: 'True', name: 'True' },
          { id: 'False', name: 'False' },
        ]}
        alwaysOn
        InputLabelProps={{ shrink: true }}
      />
      <AutocompleteArrayInput
        source="qs_excl_states"
        optionText="abbreviation"
        optionValue="abbreviation"
        label="Excluded States"
        choices={US_STATES}
        alwaysOn
      />
    </Filter>
  )
}

export const NextDestinations = (props: any) => {
  return (
    <Labeled label="Next Destinations">
      <ArrayField source={props.source}>
        <SingleFieldList linkType={false}>
          <ChipField source="label" />
        </SingleFieldList>
      </ArrayField>
    </Labeled>
  )
}

const ExpandedRow = (props: any) => {
  const record = useRecordContext()

  record.qs_dispatch_info.next_destinations.forEach(function (destination: any) {
    destination.label = `${destination.location} @ $${destination.estimated_rate_per_mile.toFixed(2)}/mi`
  })

  return (
    <SimpleShowLayout>
      <CopyToClipboardButton
        text={`${getAppUrl()}/loads/details/${record.id}/`}
        label="Click to copy shareable link"
        {...props}
      />
      <NumberField source="qs_optimizer_score" label="Dispatch AI Score" options={{ style: 'percent' }} />
      <NumberField
        source="qs_total_expected_rate"
        label={'Revenue Through ' + record.qs_dispatch_info.end_date}
        options={{ style: 'currency', currency: 'USD', maximumFractionDigits: 0 }}
      />
      <FunctionField
        label="Notes"
        render={(record: any) => {
          if (record.qs_imputed_delivery_date !== record.qs_dest_delivery_date) {
            return `Our estimated delivery date of ${record.qs_imputed_delivery_date} differs from the one listed on the load`
          }
        }}
      />
      <NextDestinations source="qs_dispatch_info.next_destinations" />
    </SimpleShowLayout>
  )
}

const RegenerateSearchButton = ({ record, redirectTo, ...props }: any) => {
  const initialValues = SearchRecordtoForm(record)

  const regenerate_search = () => {
    redirectTo('/searches/create', '', null, {}, { initial_values: initialValues })
  }

  return (
    <Button label="Regenerate search with same parameters" onClick={regenerate_search} {...props}>
      <UpdateIcon />
    </Button>
  )
}

const QuerySearchLoadList = (props: any) => {
  const record = useRecordContext()
  const filters = get(record, 'console_metadata.filters', {})
  const label = get(record, 'console_metadata.label', null)

  return (
    <CTList {...props} filterDefaultValues={{ ...filters, label: label }}>
      {props.children}
    </CTList>
  )
}

const SearchShow = (props: any) => {
  const redirectTo = useRedirect()

  return (
    <Show {...props}>
      <SimpleShowLayout>
        <RegenerateSearchButton redirectTo={redirectTo} />
        <br></br>
        <StatusTextField source="status" />
        <DateField
          source="filter_options.origin_pickup_date__min"
          label="Pickup Date"
          options={{ timeZoneName: 'short' }}
        />
        <TextField source="filter_options.origin_location" label="Origin" />
        <FunctionField
          render={(record: any) =>
            record.filter_options.dest_location ? record.filter_options.dest_location : 'Anywhere'
          }
          label="Destination"
        />
        <TextField source="num_loads" label="Number of Loads" />
        <Labeled label="Previous Delivery">
          <CustomReferenceDateField
            id="console_metadata.load_id"
            reference="loads"
            label="Previous Delivery"
            source="dest_delivery_date"
            condition={(preload: boolean) => !preload}
          />
        </Labeled>
        <Labeled label="Next Pickup">
          <CustomReferenceDateField
            id="console_metadata.load_id"
            reference="loads"
            label="Next Pickup"
            source="origin_pickup_date"
            condition={(preload: boolean) => preload}
          />
        </Labeled>
        <Divider />
        <ResourceContextProvider value="search-loads">
          <QuerySearchLoadList
            bulkActionButtons={false}
            label="Loads"
            resource="search-loads"
            basePath="/search-loads"
            filter={{ query_search_id: props.id }}
            pagination={<ListPagination />}
            perPage={25}
            filters={<LoadFilters />}
            empty={false}
            sort={{ field: 'qs_optimizer_score', order: 'DESC' }}
          >
            <Datagrid
              optimized
              rowClick={
                /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
                (id, basePath, record) => {
                  window.open(`/loads/${id}/show`)
                  return ''
                }
              }
              expand={<ExpandedRow />}
            >
              <FunctionField
                source="qs_optimizer_score"
                label="Dispatch AI Grade"
                sortByOrder="DESC"
                render={(record: any) => {
                  if (record.qs_optimizer_score >= 0.95) {
                    return 'A+'
                  } else if (record.qs_optimizer_score >= 0.9) {
                    return 'A'
                  } else if (record.qs_optimizer_score >= 0.85) {
                    return 'B+'
                  } else if (record.qs_optimizer_score >= 0.8) {
                    return 'B'
                  } else if (record.qs_optimizer_score >= 0.75) {
                    return 'C+'
                  } else if (record.qs_optimizer_score >= 0.7) {
                    return 'C'
                  } else if (record.qs_optimizer_score >= 0.65) {
                    return 'D+'
                  } else if (record.qs_optimizer_score >= 0.6) {
                    return 'D'
                  } else {
                    return 'F'
                  }
                }}
              />
              <LocalizedDateTimeField
                source="qs_pickup_window_start"
                label="Pickup Window Start"
                timezone_field="qs_origin_location_timezone"
                options={{
                  year: '2-digit',
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                  timeZoneName: 'short',
                }}
              />
              <LocalizedDateTimeField
                source="qs_pickup_window_end"
                label="Pickup Window End"
                timezone_field="qs_origin_location_timezone"
                options={{
                  year: '2-digit',
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                  timeZoneName: 'short',
                }}
              />
              <LocalizedDateTimeField
                source="qs_delivery_window_start"
                label="Delivery Window Start"
                timezone_field="qs_dest_location_timezone"
                options={{
                  year: '2-digit',
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                  timeZoneName: 'short',
                }}
              />
              <LocalizedDateTimeField
                source="qs_delivery_window_end"
                label="Delivery Window End"
                timezone_field="qs_dest_location_timezone"
                options={{
                  year: '2-digit',
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                  timeZoneName: 'short',
                }}
              />
              <TextField source="qs_origin_location" label="Pickup Location" />
              <TextField source="qs_dest_location" label="Delivery Location" />
              <FunctionField
                source="broker.name"
                label="Broker"
                onClick={(e) => e.stopPropagation()}
                render={(record: any) => {
                  if (record.broker) {
                    const broker = record.broker

                    const emailAddress = record.contact_email || broker.booking_contact_email

                    const emailSubject = `${record.qs_origin_location} to ${
                      record.qs_dest_location
                    } - Load ID : ${record.source_human_id || record.source_id}<br/><br/>`
                    const emailSubjectWithoutHtml = emailSubject.replace(/<\/?[^>]+(>|$)/g, '').trim()

                    const emailBody = `
${record.qs_origin_location} to ${record.qs_dest_location} <br/><br/>
<strong>Load ID :</strong> ${record.source_human_id || record.source_id} <br/><br/>
Can I get more information on this load?
`
                    const emailBodyWithoutHtml = emailBody.replace(/<\/?[^>]+(>|$)/g, '').trim()

                    const mailTo = `${emailAddress}?subject=${encodeURIComponent(
                      emailSubjectWithoutHtml
                    )}&body=${encodeURIComponent(emailBodyWithoutHtml)}`

                    if (emailAddress) {
                      return <ExternalLink href={`mailto:${mailTo}`}>{broker.name}</ExternalLink>
                    } else {
                      return broker.name
                    }
                  }
                }}
              />
              <NumberField source="qs_origin_deadhead_mi" label="Origin Deadhead" />
              <NumberField source="qs_loaded_mi" label="Loaded Miles" />
              <NumberField source="qs_total_mi" label="Total Miles" />
              <StatusNumberField
                source="qs_trip_rate"
                status_source="qs_trip_rate_is_actual"
                label="Trip Rate"
                options={{ style: 'currency', currency: 'USD' }}
              />
              <NumberField
                source="qs_rate_per_total_mi"
                label="Rate Per Total Mile"
                options={{ style: 'currency', currency: 'USD' }}
              />
              <CustomBooleanField source="qs_instant_book" label="Instant Book" />
              <FunctionField
                source="contact_phone"
                label="Contact Phone"
                onClick={(e) => e.stopPropagation()}
                render={(record) => {
                  const phoneNumber = record?.contact_phone
                    ? record.contact_phone
                    : record?.broker?.booking_contact_phone

                  return <ExternalLink href={`tel:${phoneNumber}`}>{phoneNumber}</ExternalLink>
                }}
              />
            </Datagrid>
          </QuerySearchLoadList>
        </ResourceContextProvider>
      </SimpleShowLayout>
    </Show>
  )
}

export default SearchShow
